/**
 * プランの料金と人数割の「適用請求月」（2026-09-30）。
 *
 * 前は「いつから」を日付で選ばせていた。請求は月ごとで、各月はその月の1日の時点の
 * 料金を使うので、日付だと「今日のまま保存しても今月には効かない」という分かりにくい
 * 形になっていた（利用者「いつからっていりますか？…ちょっと意味がわからない」）。
 * いまは「どの月の請求からこの料金を使うか」を月で選び、その月の1日（YYYY-MM-01）を送る。
 * 既定は来月。先の月も、料金が無くて確定できなかった過去の月も選べる。
 *
 * **記録の日付から「何年何月分から使われるか」を出すときは billedMonthOf を通す。**
 * 月の1日ならその月、それ以外（日付の欄だった前の画面が残した記録）は翌月。
 * 確定と同じ決まり（その月の1日以前で最新の記録を使う）。
 *
 * **欄は選んでいる月に使われる値にする（2026-09-30 の見直し）。**一覧は月ごとの料金と組を返す
 * （priceSchedule / volumeDiscountSchedule）。月を選んだら entryOfMonth でその月の値を引いて入れる。
 *
 * 文：C:\biz\claude\_local_run\spec\S5の直し\Gemini_依頼と答え_プランの料金のいつからを月で選ぶ_2026-09-30.md
 *     （M-C・M-D・M-E・M-F）と
 *     C:\biz\claude\_local_run\spec\S5の直し\Gemini_依頼と答え_適用請求月の続き_欄と小窓と保存の結果とヘルプ_2026-09-30.md
 *     （AM-1〜AM-8・AM-14）。どれも Gemini と決めた文で、一字も変えずに入れてある。
 *
 * どこから見えるか：「PREIKA運営」→「請求管理」→ 画面下部「プランの料金」の
 *   「適用請求月」の列・料金の欄・プラン名の下・人数割の列と［人数割設定］の小窓・［保存］の結果。
 */

/**
 * AM-1：「適用請求月」の見出しの下の説明。表の見出しの下と小窓の欄の下の両方。
 *
 * **月は入れない。**月を差し込むと、表では全部の行で共有する見出しが来月に固定になり、行の月を
 * 9月にしても10月を選べと読めた。小窓では選んでいる月に付いてきて、もう選んでいる月をもう一度
 * 選べと言っていた。月は欄そのものに出ている。
 */
export const APPLIED_MONTH_NOTE = '設定を適用する請求月を選択してください。'

/** AM-2a：プラン名の下の料金の行（その月に最低月額がある）。 */
export const NEXT_PRICE_WITH_MINIMUM_TEMPLATE = '（{年}年{月}月分から 基本料 {基本料}円・1人あたり {単価}円・最低月額 {最低月額}円）'

/**
 * AM-2b：プラン名の下の料金の行（その月は最低月額なし）。並べた月のどれかに最低月額があるときだけ使う
 * （無いと、前の月の最低月額が続くのか、その月から外れるのかが読めない）。どの月にも無ければ M-D。
 */
export const NEXT_PRICE_WITHOUT_MINIMUM_TEMPLATE = '（{年}年{月}月分から 基本料 {基本料}円・1人あたり {単価}円・最低月額 なし）'

/**
 * AM-5：保存の結果の後ろに足す。選んだ月から後にこのプランの確定済みの請求があるとき。
 * {年}{月} は選んだ適用請求月（後の月の料金がある M-F のときも、選んだ月のまま）。
 */
export const FIXED_BILLING_TEMPLATE = 'なお、すでに確定済みの {年}年{月}月 分以降の請求金額は変更されません。'

/** AM-6：人数割の保存の結果。 */
export const TIERS_SAVED_TEMPLATE = '{プラン名} の人数割を設定しました。この設定は {年}年{月}月 分の請求から適用されます。'

/** AM-7：人数割の保存の結果で、後の月の組がもうあるとき（{年}{月} は後の月）。 */
export const TIERS_SAVED_LATER_TEMPLATE = '{プラン名} の人数割を設定しました。なお、すでに {年}年{月}月 分以降の人数割設定が存在するため、その月以降の請求にはそちらの設定が適用されます。'

/** M-C：プラン名の下（と人数割の要約の後ろ）の、今の料金の月。 */
export const APPLIED_SINCE_TEMPLATE = '（{年}年{月}月分〜適用）'

/** M-D：先の月の料金があるときに足す表示。 */
export const NEXT_PRICE_TEMPLATE = '（{年}年{月}月分から 基本料 {基本料}円・1人あたり {単価}円）'

/** M-E：料金の保存の結果。 */
export const PRICE_SAVED_TEMPLATE = '{プラン名} の料金を保存しました。この設定は {年}年{月}月 分の請求から適用されます。'

/**
 * K2：料金の保存の結果に、保存した金額も出す形（2026-10-10）。金額は ¥10,000 の形。
 * 選んだ月より後の月の料金がもうあるとき（M-F）は、今までどおり M-F の文。
 */
export const PRICE_SAVED_WITH_AMOUNTS_TEMPLATE = '{プラン名}の料金（基本料：{基本料}／1人あたり：{単価}）を保存しました。この設定は {年}年{月}月 分の請求から適用されます。'

/**
 * M3：基本料も1人あたりも0円で保存したとき（無料プランではないプラン）に、保存の結果に続ける注意。
 * 2026-10-10 に K3 から替えた。K3 は「適用される月の請求は確定されません」と言い切っていたが、
 * 月額が0円でも正の調整分があれば確定されるので、事実と違った（監査 BF-2）。
 */
export const ZERO_PRICE_SAVED_NOTE = '料金が基本料・1人あたりともに0円に設定されたため、調整分などの追加請求がない場合、この設定が適用される月の請求確定は見送られます。無料にする場合は「無料プランとして設定する」で追加したプランか、初期特別価格の0円をご利用ください。'

/** M-F：選んだ月より後の月の料金がもうあるときの保存の結果（{年}{月} は後の月）。 */
export const PRICE_SAVED_LATER_TEMPLATE = '{プラン名} の料金を保存しました。なお、すでに {年}年{月}月 分以降の料金設定が存在するため、その月以降の請求にはそちらの料金が適用されます。'

/** AM-4：人数割の列の先の月の組（{要約} は人数割の要約）。 */
export const NEXT_TIERS_TEMPLATE = '（{年}年{月}月分から：{要約}）'

/** AM-14：月の欄を空にして保存したとき。 */
export const EMPTY_MONTH_MESSAGE = '適用請求月を選択してください。'

const MONTH_VALUE = /^(\d{4})-(0[1-9]|1[0-2])$/

const two = n => (n < 10 ? '0' + n : String(n))

/** 年と月を差し込む。**月は0を付けない**（2026年9月。見送りの理由の文とそろえる）。 */
export function fillMonth (template, month) {
  return template.split('{年}').join(String(month.year)).split('{月}').join(String(month.month))
}

/**
 * 記録の適用日（YYYY-MM-DD）が最初に使われる請求の月 { year, month }。
 * 1日ならその月、それ以外は翌月（確定と同じ決まり）。読めなければ null。
 */
export function billedMonthOf (dateText) {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(dateText || ''))
  if (!match) return null
  let year = Number(match[1])
  let month = Number(match[2])
  if (Number(match[3]) !== 1) {
    month += 1
    if (month > 12) { month = 1; year += 1 }
  }
  return { year, month }
}

/** その日から数えて offset か月先の月（<input type="month"> の値 YYYY-MM）。 */
export function monthValueOf (date, offset = 0) {
  const shifted = new Date(date.getFullYear(), date.getMonth() + offset, 1)
  return shifted.getFullYear() + '-' + two(shifted.getMonth() + 1)
}

/** 「適用請求月」の既定：来月。 */
export function nextMonthValue (date = new Date()) {
  return monthValueOf(date, 1)
}

/** 月の欄の値（YYYY-MM）を、送る値（その月の1日 YYYY-MM-01）にする。形が違えば空文字。 */
export function monthStartOf (monthValue) {
  const text = String(monthValue || '').trim()
  return MONTH_VALUE.test(text) ? text + '-01' : ''
}

/** 月の欄の値（YYYY-MM）を { year, month } にする。形が違えば null。 */
export function monthOfValue (monthValue) {
  const match = MONTH_VALUE.exec(String(monthValue || '').trim())
  return match ? { year: Number(match[1]), month: Number(match[2]) } : null
}

/** M-C。記録の適用日が無ければ空文字（添えない）。 */
export function appliedSinceText (dateText) {
  const month = billedMonthOf(dateText)
  return month ? fillMonth(APPLIED_SINCE_TEMPLATE, month) : ''
}

/** 円の書き方（3桁区切り）。 */
function yenText (value) {
  return Number(value).toLocaleString('ja-JP')
}

const isBlank = value => value === null || value === undefined || String(value).trim() === ''

const dayOf = entry => String((entry && entry.appliedOn) || '').slice(0, 10)

/**
 * **その月に使われる記録**（2026-09-30 の見直し）。
 *
 * 一覧の行の priceSchedule（月ごとの料金）か volumeDiscountSchedule（月ごとの組）から、
 * その月の1日（YYYY-MM-01）以前でいちばん新しい月の記録を返す。確定と同じ決まり
 * （その月の1日以前で最新の記録）。back は記録が最初に使われる月ごとに1本ずつ、その月に
 * 使う値を返すので、ここは並びから選ぶだけ。無ければ null（その月は料金が無い／割引なし）。
 *
 * 「プランの料金」の行の欄と［人数割設定］の小窓は、これで「選んでいる適用請求月に使われる値」を
 * 入れる。前は欄がいつも今月の値で、月の欄の既定は来月だったので、来月分を保存してある行で
 * 1か所だけ直して保存すると、来月分が今月の値で置き換わった。
 */
export function entryOfMonth (schedule, monthStart) {
  if (!Array.isArray(schedule) || !monthStart) return null
  return schedule.reduce((found, entry) => {
    const day = dayOf(entry)
    if (!day || day > monthStart) return found
    return !found || day >= dayOf(found) ? entry : found
  }, null)
}

/** 月ごとの並びのうち、その月の1日より後の記録（月の順）。 */
export function entriesAfter (schedule, monthStart) {
  if (!Array.isArray(schedule) || !monthStart) return []
  return schedule
    .filter(entry => dayOf(entry) > monthStart)
    .sort((a, b) => (dayOf(a) < dayOf(b) ? -1 : dayOf(a) > dayOf(b) ? 1 : 0))
}

const amountKey = value => (isBlank(value) ? '' : String(Number(String(value).trim())))

/** 料金の記録どうしが同じ値か（基本料・1人あたり・最低月額）。 */
function samePrice (a, b) {
  if (!a || !b) return false
  return amountKey(a.basePrice) === amountKey(b.basePrice)
    && amountKey(a.pricePerUser) === amountKey(b.pricePerUser)
    && amountKey(a.minimumMonthlyAmount) === amountKey(b.minimumMonthlyAmount)
}

/**
 * **プラン名の下に並べる料金の行**（M-D の形。最低月額は AM-2a／AM-2b。2026-09-30 の見直し）。
 *
 * 先の月の料金（今月より後の月の記録）が1つでもあれば、今月分と先の月分を1行ずつ全部並べる
 * （今月分の行も M-D の形のまま。AM-3）。
 * 行の欄は選んでいる月の値になるので、今月分も文で出さないと、今の料金が画面から消える。
 * 前の月と同じ値の月は並べない（何も変わらない行になる）。先の月が無ければ空の並び
 * （画面は M-C「（何年何月分〜適用）」だけを出す。欄の値がそのまま今の料金なので）。
 *
 * 前は先の月の最初の1か月の基本料と1人あたりだけで、2つ目以降の月と先の月の最低月額は
 * 画面のどこにも出ず、今と同じ値の記録でも出ていた。
 *
 * @param schedule 一覧の行の priceSchedule
 * @param thisMonthStart 今月の1日（YYYY-MM-01）
 */
export function priceScheduleLines (schedule, thisMonthStart) {
  const current = entryOfMonth(schedule, thisMonthStart)
  const coming = []
  let previous = current
  entriesAfter(schedule, thisMonthStart).forEach(entry => {
    if (samePrice(previous, entry)) return
    coming.push(entry)
    previous = entry
  })
  if (!coming.length) return []
  const lines = (current ? [current] : []).concat(coming)
  const anyMinimum = lines.some(entry => !isBlank(entry.minimumMonthlyAmount))
  return lines.map(entry => {
    const month = billedMonthOf(entry.appliedOn)
    const template = !isBlank(entry.minimumMonthlyAmount)
      ? NEXT_PRICE_WITH_MINIMUM_TEMPLATE
      : anyMinimum ? NEXT_PRICE_WITHOUT_MINIMUM_TEMPLATE : NEXT_PRICE_TEMPLATE
    return fillMonth(template, month)
      .replace('{基本料}', () => yenText(entry.basePrice))
      .replace('{単価}', () => yenText(entry.pricePerUser))
      .replace('{最低月額}', () => (isBlank(entry.minimumMonthlyAmount) ? '' : yenText(entry.minimumMonthlyAmount)))
  })
}

/**
 * 保存の結果（料金は M-E か M-F、人数割は AM-6 か AM-7）。
 * @param appliedOn 保存した月の1日（YYYY-MM-01）
 * @param laterAppliedOn back が返した「選んだ月より後で最初の記録の日」。あれば後の月の形
 * @param fixedBillingExists back が返した「その月から後にこのプランの確定済みの請求があるか」。
 *        あれば選んだ月を入れた AM-5 を後ろに足す（前は確定済みの月を選んでも
 *        「この設定は その月分の請求から適用されます。」とだけ出て、直せたと読めた）
 */
function savedText (templates, planName, appliedOn, laterAppliedOn, fixedBillingExists) {
  const later = billedMonthOf(laterAppliedOn)
  const template = later ? templates.later : templates.saved
  const selected = billedMonthOf(appliedOn)
  const month = later || selected
  // 月を先に差し込む（プラン名に「{年}」などが入っていても崩れないように）。
  // プラン名は関数で返す。文字列で渡すと「$&」などが特別な意味になり、文が崩れる。
  return fillMonth(template, month).replace('{プラン名}', () => planName)
    + (fixedBillingExists === true && selected ? fillMonth(FIXED_BILLING_TEMPLATE, selected) : '')
}

/** 画面のほかの所と同じ金額の書き方（¥10,000）。 */
function yenMarkText (value) {
  return '¥' + Number(value || 0).toLocaleString('ja-JP')
}

/**
 * 料金の保存の結果（K2 か M-F。確定済みの請求があれば AM-5 を足し、0円で保存したら K3 を足す）。
 *
 * @param saved 保存した値 {@code { basePrice, pricePerUser, freePlan }}。渡さなければ、
 *        金額を出さない前の形（M-E）のまま（金額を知らない呼び方を壊さない）
 */
export function priceSavedText (planName, appliedOn, laterAppliedOn, fixedBillingExists, saved) {
  if (!saved || isBlank(saved.basePrice) || isBlank(saved.pricePerUser)) {
    return savedText({ saved: PRICE_SAVED_TEMPLATE, later: PRICE_SAVED_LATER_TEMPLATE },
      planName, appliedOn, laterAppliedOn, fixedBillingExists)
  }
  // 金額を先に差し込む（月・プラン名より先。プラン名に「{基本料}」が入っていても崩れないように）。
  const withAmounts = PRICE_SAVED_WITH_AMOUNTS_TEMPLATE
    .replace('{基本料}', () => yenMarkText(saved.basePrice))
    .replace('{単価}', () => yenMarkText(saved.pricePerUser))
  const text = savedText({ saved: withAmounts, later: PRICE_SAVED_LATER_TEMPLATE },
    planName, appliedOn, laterAppliedOn, fixedBillingExists)
  const zero = Number(saved.basePrice) === 0 && Number(saved.pricePerUser) === 0
  return zero && saved.freePlan !== true ? text + ZERO_PRICE_SAVED_NOTE : text
}

/** 人数割の保存の結果（AM-6 か AM-7。料金と同じ出し分けで、確定済みの請求があれば AM-5 を足す）。 */
export function tiersSavedText (planName, appliedOn, laterAppliedOn, fixedBillingExists) {
  return savedText({ saved: TIERS_SAVED_TEMPLATE, later: TIERS_SAVED_LATER_TEMPLATE },
    planName, appliedOn, laterAppliedOn, fixedBillingExists)
}
